import React from 'react';
import {
  Box, Divider, Link, Typography,
} from '@mui/material';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import { QuestionGroup } from './data';
import { colors, contentBlockShadow } from '../../../theme';
import { track } from '../analytics';

/**
 * The community rehoming partners. Deliberately styled as external link cards —
 * the one sanctioned exception to "existing Hub components/styles only".
 */
const REHOMING_PARTNERS = [
  {
    title: 'Rehome by Adopt-a-Pet',
    sub: 'List your pet and connect with a new family. Get 90% off with code PETHELP2026',
    url: 'https://rehome.adoptapet.com/',
  },
  {
    title: 'Home To Home',
    sub: 'Create a free listing and screen adopters yourself',
    url: 'https://home-home.org/',
  },
];

function QuestionGroupRow({
  group, expanded, onToggle,
}: { group: QuestionGroup; expanded: boolean; onToggle: () => void }) {
  return (
    <>
      <Box
        onClick={() => {
          track('adopter_question_toggle', { reason: group.reason });
          onToggle();
        }}
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1.5,
          py: { xs: 1.75, md: '15px' },
          px: { xs: 0.25, md: 0.5 },
          cursor: 'pointer',
        }}
      >
        <Typography sx={{
          // QA: match the Resources section's row-title style exactly.
          flex: 1, minWidth: 0, fontWeight: 500, fontSize: 16, lineHeight: 1.3, color: '#000000DE',
        }}
        >
          {group.intro}
        </Typography>
        {expanded
          ? <ExpandLessIcon sx={{ flexShrink: 0, fontSize: 22, color: 'text.secondary' }} />
          : <ExpandMoreIcon sx={{ flexShrink: 0, fontSize: 22, color: 'text.secondary' }} />}
      </Box>
      {expanded && (
        <Box sx={{ pb: { xs: 2, md: 2.25 }, px: { xs: 0.25, md: 0.5 } }}>
          <Box component="ul" sx={{ m: 0, pl: 2.25 }}>
            {group.questions.map((question) => (
              <Typography
                key={question}
                component="li"
                sx={{
                  // QA: match the Resources section's bullet style exactly
                  // (13.5px, #000000DE, 6px gap between bullets).
                  fontSize: 13.5,
                  color: '#000000DE',
                  lineHeight: 1.45,
                  '&:not(:last-of-type)': { mb: 0.75 },
                }}
              >
                {question}
              </Typography>
            ))}
          </Box>
        </Box>
      )}
    </>
  );
}

interface RehomingSectionProps {
  petName: string;
  orgName: string;
  /** One group per reported reason ({pet} tokens already filled). */
  questionGroups: QuestionGroup[];
}

/**
 * "Rehoming {pet}" — community rehoming partner links plus the reason-driven
 * "Questions adopters will ask" accordion (first group open by default).
 */
function RehomingSection({ petName, orgName, questionGroups }: RehomingSectionProps) {
  const [expanded, setExpanded] = React.useState<Record<number, boolean>>({ 0: true });
  const toggle = (i: number) => setExpanded((s) => ({ ...s, [i]: !s[i] }));

  return (
    <Box
      sx={{
        bgcolor: '#fff',
        borderRadius: '12px',
        boxShadow: contentBlockShadow,
        p: { xs: '20px 16px', md: '26px 30px' },
      }}
    >
      <Typography variant="h5">{`Rehoming ${petName}`}</Typography>
      <Typography sx={{
        fontSize: { xs: 13.5, md: 14.5 }, lineHeight: 1.55, color: 'text.secondary', mt: 1,
      }}
      >
        {`Instead of surrendering ${petName} to ${orgName}, try to rehome within the community. These services `
          + `are free, keep ${petName} in a home during the transition, and let you review and choose the adopter `
          + 'yourself.'}
      </Typography>
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
          gap: 1.5,
          mt: 2.25,
        }}
      >
        {REHOMING_PARTNERS.map((partner) => (
          <Link
            key={partner.url}
            href={partner.url}
            target="_blank"
            rel="noopener"
            underline="none"
            onClick={() => track('rehoming_partner_click', { partner: partner.title })}
            sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: 0.75,
              border: `1px solid ${colors.grey.divider}`,
              borderRadius: '10px',
              p: 2,
              transition: 'border-color 150ms, box-shadow 150ms',
              '&:hover': { borderColor: colors.primary[400], boxShadow: contentBlockShadow },
            }}
          >
            <Typography sx={{
              fontWeight: 600, fontSize: 15, lineHeight: 1.35, color: 'text.primary',
            }}
            >
              {partner.title}
            </Typography>
            <Typography sx={{
              fontSize: 13, lineHeight: 1.45, color: 'text.secondary', flex: 1,
            }}
            >
              {partner.sub}
            </Typography>
            <Box sx={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 0.75,
              fontWeight: 600,
              fontSize: 13.5,
              color: colors.primary.main,
            }}
            >
              Open
              <OpenInNewIcon sx={{ fontSize: 15 }} />
            </Box>
          </Link>
        ))}
      </Box>
      <Box sx={{ mt: { xs: 3, md: 3.5 } }}>
        <Typography sx={{ fontWeight: 700, fontSize: { xs: 16, md: 17 }, lineHeight: 1.3 }}>
          {`Questions adopters will ask about ${petName}`}
        </Typography>
        <Typography sx={{
          fontSize: { xs: 13, md: 14 }, lineHeight: 1.55, color: 'text.secondary', mt: 0.75, mb: { xs: 1, md: 1.5 },
        }}
        >
          {`Thinking through these ahead of time makes conversations easier and helps ${petName} land in a home `
            + 'that fits.'}
        </Typography>
        {questionGroups.map((group, i) => (
          <React.Fragment key={group.reason}>
            {i > 0 && <Divider />}
            <QuestionGroupRow group={group} expanded={!!expanded[i]} onToggle={() => toggle(i)} />
          </React.Fragment>
        ))}
      </Box>
    </Box>
  );
}

export default RehomingSection;
